import _ from "lodash";
import React, { useState, useEffect } from "react";
import { useI18n } from "Hooks";

// design system
import { Button, Modal, IconButton, Tooltip } from "@toddle-edu/ds-web";
import { EyeOutlined } from "@toddle-edu/ds-icons";

// components
import MarkdownView from "UIComponents/MarkdownView";
import FullScreenLoader from "UIComponents/FullScreenLoader";

// modules
import {
  isMaxScoreValid,
  validateAIEvaluationEligibility,
} from "ClassRoom/modules/ClassRoomModule";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getGlobalHeaderWiseScreenRevampStatus } from "Course/modules/CourseModule";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { updateAIDataInRedux } from "Assessments/modules/AssessmentModule";
import { updateTitleFromLTIAttachment } from "Assessments/modules/AssessmentModule/AssessmentThunks";

import {
  getAssessmentQuestionsFromCache,
  getAssessmentQuestionsFromCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import {
  getAssessmentQuestionsQueryVariables,
  getAssessmentTotalScore,
  getAssessmentTotalScoreV2,
} from "Worksheet/modules/WorksheetUtils";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

// utils
import { delay } from "Utils";
import {
  getBackDateAssignmentCoursesV2,
  getShowUpdateAITutorModal,
} from "../Utils";
import {
  getIsLearningCoursesEnabled,
  getIsBlueprintCourse,
} from "LearningCourses/modules/Utils";
import { getSelectedLearningCourse } from "LearningCourse/modules/Utils";
import { getIsProSchool } from "UnitPlans/modules/Utils";
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";

// constants
import {
  ASSESSMENT_TYPE_AI_TUTOR,
  TODDLE_LIBRARY as ACTIVE_TAB_TODDLE_LIBRARY,
  TASK_TYPE_AI_TUTOR,
} from "Constants/stringConstants";

// third party
import { connect } from "react-redux";

// scss
import classes from "./CompactUIFooter.scss";

const initialMaxScoreChangeModalParams = {
  showMaxScoreChangedModal: false,
  isAssignOrUpdateClicked: false,
};

const shiftEnterShortcut = { code: "Enter", shiftKey: true };

const CompactUIFooter = props => {
  const {
    mode,
    state,
    isFirstStep,
    isLastStep,
    onClickDone,
    onClickCancel,
    onClickPrevious,
    setShowSettingsModal,
    customFooterButton,
    renderAsComponent = true,
    assessmentId,
    isMaxScoreValid,
    onClickNext,
    isNextDisabled,
    resourceSelection,
    onResourceSelectionDone,
    initialMaxScore,
    toggleErrorModal,
    checkIsDueDateBeforeScheduleDate,
    setBackDateModalParams,
    courses,
    showCancelButton = true,
    containerStyle = {},
    innerContainerStyle = {},
    isGeneratingAIQuestions,
    collaboratorsFilter,
    currentDialogueAction = "",
    showAITutorStudentPreviewModal,
    updateAIDataInRedux,
    assessmentType,
    taskTypeGroup,
    rightContainerStyle,
    aiTutorSelectedTab,
    isAITutorUpdated,
    setCurrentDialogWithAlert,
    assignmentId,
    setIsLoading,
    parentId,
    parentType,
    portalType,
    isWorksheetCreationStep,
    assignmentData,
    assignmentLevelPreferences,
    isExtracting,
    isBlueprintCourse,
    isLearningCoursesEnabled,
    validateAIEvaluationEligibility,
    isQueriesV2Enabled,
    isUsingNewGradingSetupInCourses,
    courseId,
    updateTitleFromLTIAttachment,
  } = props;

  const { t } = useI18n();

  const isGlobalHeaderEnabled = getGlobalHeaderWiseScreenRevampStatus();
  const isAITutorModalitiesEnabled = getIsAITutorModalitiesEnabled();

  const [maxScoreChangedModalParams, setMaxScoreChangedModalParams] = useState(
    initialMaxScoreChangeModalParams
  );
  const [showFullScreenLoaderLocal, setShowFullScreenLoaderLocal] = useState(
    false
  );

  const [missingCriteriaModalData, setMissingCriteriaModalData] = useState({});
  const isProSchool = getIsProSchool();
  const isDraftMode = mode === "create" || state === "DRAFT";
  const skipCheckForMaxScoreChanged = isDraftMode || state === "SCHEDULED";
  // Hide assign button in 2.0 if assessment creation is done in blueprint course...
  // because blueprint courses are not linked with classes so assign button is not needed
  // Also make save as draft button as save button with variant as progressive
  const hideAssignButton =
    (isLearningCoursesEnabled && isBlueprintCourse) || isProSchool;

  const testIdSuffix =
    !renderAsComponent || customFooterButton
      ? "unitPlanAssessmentCreationFooter"
      : resourceSelection
      ? "assignmentCreation:resourceSelectionFooter"
      : "assignmentCreationFooter";

  const isAITutor =
    taskTypeGroup === TASK_TYPE_AI_TUTOR ||
    assessmentType === ASSESSMENT_TYPE_AI_TUTOR;

  const shouldUpdateAITabState = isAITutor && aiTutorSelectedTab === "CHAT";

  const {
    showMaxScoreChangedModal,
    isAssignOrUpdateClicked,
  } = maxScoreChangedModalParams;

  const isValid = async params => {
    let assessmentDetails = getAssessmentDetailsFromCache({
      id: assessmentId,
      collaboratorsFilter,
    });

    const isAssessmentValid = _.isFunction(props?.isValid)
      ? props?.isValid?.(params) &&
        isMaxScoreValid({
          assessmentDetails,
          isUsingNewGradingSetupInCourses,
          courseId,
        })
      : isMaxScoreValid({
          assessmentDetails,
          isUsingNewGradingSetupInCourses,
          courseId,
        });

    if (!isAssessmentValid) return false;

    const { allFields } = assessmentDetails || {};

    const isAIEvaluationEnabled = _.get(
      _.find(allFields, { uid: "aiEvaluation" }),
      "value",
      false
    );

    if (isAIEvaluationEnabled && _.isFunction(props?.isValid)) {
      setShowFullScreenLoaderLocal(true);
      await delay(1000); // updateAssessment take some time to succeed, so we need to wait for the updated links in description field
    }

    assessmentDetails = getAssessmentDetailsFromCache({
      id: assessmentId,
      collaboratorsFilter,
    });

    if (isAIEvaluationEnabled && _.isFunction(props?.isValid)) {
      try {
        const {
          isValid: isAIEvaluationEligible,
          title: missingCriteriaModalTitle,
          description: missingCriteriaModalDescription,
        } = await validateAIEvaluationEligibility({
          t,
          assessmentDetails,
        });

        if (!isAIEvaluationEligible) {
          setMissingCriteriaModalData({
            title: missingCriteriaModalTitle,
            description: missingCriteriaModalDescription,
          });
          return false;
        }
      } finally {
        setShowFullScreenLoaderLocal(false);
      }
    }

    return isAssessmentValid;
  };

  const checkIsMaxScoreChanged = () => {
    const assessmentDetails = getAssessmentDetailsFromCache({
      id: assessmentId,
      collaboratorsFilter,
    });
    const assessmentToolEdges = _.get(
      assessmentDetails,
      "assessmentTools.edges",
      []
    );
    const assessmentTools = _.map(assessmentToolEdges, ({ node }) => node);
    const maxScoreTool =
      _.find(assessmentTools, {
        assessmentToolType: "SCORE",
      }) || {};

    if (!_.isEmpty(maxScoreTool)) {
      const scoreToolData = _.get(
        maxScoreTool,
        "assessmentToolItem.scoreToolData",
        {}
      );
      const currentMaxScore = _.get(scoreToolData, "maxScore", 0);

      if (isWorksheetCreationStep) {
        let totalScore;
        if (isQueriesV2Enabled) {
          // fetch assessment questions
          const assessmentQuestionsQueryVariables = {
            id: parentId,
            type: parentType,
            portalType,
          };
          const assessmentQuestions = getAssessmentQuestionsFromCacheV2(
            assessmentQuestionsQueryVariables
          );
          totalScore = getAssessmentTotalScoreV2({
            data: _.get(assessmentQuestions, "questions.edges", []),
          });
        } else {
          //WORKSHEET_RESTRUCTURING: Remove else
          const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
            {
              parentId,
              parentType,
              portalType,
            }
          );
          const assessmentQuestions = getAssessmentQuestionsFromCache(
            assessmentQuestionsQueryVariables
          );
          totalScore = getAssessmentTotalScore({ assessmentQuestions });
        }
        if (totalScore != currentMaxScore) {
          return true;
        }
      } else if (
        !_.isNil(initialMaxScore) &&
        initialMaxScore !== currentMaxScore
      ) {
        return true;
      }
    }

    return false;
  };

  const handleAssignOrUpdate = async () => {
    await updateTitleFromLTIAttachment({ assessmentId });

    if (await isValid({ checkForInvalidQuestions: true })) {
      if (isDraftMode) {
        setShowSettingsModal(true);
      } else {
        // --------------- //
        if (checkIsDueDateBeforeScheduleDate()) {
          toggleErrorModal({ isAssignClicked: false });
          return;
        }

        // --------------- //
        const oldCourses = _.get(assignmentData, "courses", []);
        const isBackdateAssignment = _.get(
          assignmentData,
          "isBackdateAssignment",
          false
        );

        const {
          backDateCourses,
          hasBothOpenAndDueDateBackdated,
        } = getBackDateAssignmentCoursesV2({
          courses,
          oldCourses,
          assignmentLevelPreferences,
          isBackdateAssignment,
        });
        if (!_.isEmpty(backDateCourses)) {
          setBackDateModalParams({
            buttonLabelText: isDraftMode
              ? t("common:assign")
              : t("common:update"),
            hasBothOpenAndDueDateBackdated,
            backDateCourses,
          });
          return;
        }

        // when tutor is update and few students have already started the conversation
        // then show a modal to let the teacher know that "Students will be notified that changes have been made and will be allowed to reset their session with the tutor."
        if (isAITutorUpdated && mode === "edit") {
          setIsLoading(true);
          const showUpdateAITutorModal = await getShowUpdateAITutorModal({
            assignmentId,
          });
          setIsLoading(false);

          if (showUpdateAITutorModal) {
            setCurrentDialogWithAlert("UPDATE_AI_TUTOR");
            return;
          }
        }

        onClickDone();
      }
    }
  };

  const handleReassignOrSaveAsDraft = async () => {
    if (await isValid()) {
      await updateTitleFromLTIAttachment({ assessmentId });

      if (isDraftMode) {
        if (checkIsDueDateBeforeScheduleDate()) {
          toggleErrorModal({ isAssignClicked: false });
          return;
        }
        const onClickDoneParams = {
          assessmentState: "DRAFT",
          shouldSelectAtleastOneStudent: false,
          saveAsDraftFromFooter: true,
        };
        onClickDone(onClickDoneParams);
      } else {
        setShowSettingsModal(true);
      }
    }
  };

  const onClickAssignOrUpdate = () => {
    if (!skipCheckForMaxScoreChanged && checkIsMaxScoreChanged()) {
      setMaxScoreChangedModalParams({
        showMaxScoreChangedModal: true,
        isAssignOrUpdateClicked: true,
      });
      return;
    }

    // we switch to "Tutor configuration" tab when user is in "Create with AI" tab and assign is clicked
    // else error toast is shown
    if (shouldUpdateAITabState) {
      updateAIDataInRedux({ aiTutorSelectedTab: "TASK_DETAILS" });
      setTimeout(() => {
        handleAssignOrUpdate();
      }, 75);
    } else {
      handleAssignOrUpdate();
    }
  };

  const onClickReassignOrSaveAsDraft = () => {
    if (!skipCheckForMaxScoreChanged && checkIsMaxScoreChanged()) {
      setMaxScoreChangedModalParams({
        showMaxScoreChangedModal: true,
        isAssignOrUpdateClicked: false,
      });
      return;
    }

    // we switch to "Tutor configuration" tab when user is in "Create with AI" tab and assign is clicked
    // else error toast is shown
    if (shouldUpdateAITabState) {
      updateAIDataInRedux({ aiTutorSelectedTab: "TASK_DETAILS" });
      setTimeout(() => {
        handleReassignOrSaveAsDraft();
      }, 75);
    } else {
      handleReassignOrSaveAsDraft();
    }
  };

  const onClickCancelOrBack = () => {
    if (isFirstStep) {
      onClickCancel();
    } else {
      onClickPrevious();
    }
  };

  const onClickSave = async () => {
    if (await isValid()) {
      onClickDone();
    }
  };

  const onMaxScoreChangedModalClose = () => {
    setMaxScoreChangedModalParams(initialMaxScoreChangeModalParams);
  };

  const onMaxScoreChangedModalConfirm = () => {
    onMaxScoreChangedModalClose();
    if (isAssignOrUpdateClicked) {
      handleAssignOrUpdate();
    } else {
      handleReassignOrSaveAsDraft();
    }
  };

  const toggleAITutorStudentPreviewModal = () => {
    updateAIDataInRedux({
      showAITutorStudentPreviewModal: !showAITutorStudentPreviewModal,
    });
  };

  const renderMaxScoreChangedModal = () => {
    return (
      <Modal
        width={"488px"}
        height={"max-content"}
        onClose={onMaxScoreChangedModalClose}
      >
        <Modal.Header
          title={t("classRoom:max_score_changed")}
          isClosable={true}
        />
        <Modal.Body>
          <div className={classes.message}>
            {t("classRoom:max_score_changed_message")}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footer}>
            <Button
              size="medium"
              onClick={onMaxScoreChangedModalClose}
              dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
              variant={isGlobalHeaderEnabled ? "neutral" : "outlined-subtle"}
              type="outlined"
            >
              {t("classRoom:no_keep_editing")}
            </Button>

            <Button
              size="medium"
              onClick={onMaxScoreChangedModalConfirm}
              dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
              shortcut={isGlobalHeaderEnabled ? shiftEnterShortcut : undefined}
            >
              {t("classRoom:yes_change_the_max_score")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderMissingCriteriaModal = () => {
    const onCloseMissingCriteriaModal = () => {
      setMissingCriteriaModalData({});
    };

    const { title, description } = missingCriteriaModalData || {};

    return (
      <Modal
        width="420px"
        height="fit-content"
        isOpen={!_.isEmpty(missingCriteriaModalData)}
        onClose={onCloseMissingCriteriaModal}
        dsVersion="2.0"
      >
        <Modal.Header title={title} isClosable={true} />
        <Modal.Body>
          <MarkdownView
            content={description}
            contentContainerClass={classes.markdownView}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footer}>
            <Button
              size="medium"
              onClick={onCloseMissingCriteriaModal}
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
            >
              {t("common:back_to_editing")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderFooterLeft = () => {
    return (
      <Button
        dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
        variant={isGlobalHeaderEnabled ? "neutral" : "outlined-subtle"}
        type="outlined"
        size={"medium"}
        onClick={onClickCancelOrBack}
        testId={`${testIdSuffix}-${isFirstStep ? "cancel" : "back"}`}
      >
        {isFirstStep ? t("common:cancel") : t("common:back")}
      </Button>
    );
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const shouldTruncateSaveAsDraftText =
    isAITutor && isAITutorModalitiesEnabled && isDraftMode && !hideAssignButton;

  const renderFooterRight = () => {
    // if isLastStep is false it means that there are more than 1 step,so show next button

    if (!isLastStep) {
      return (
        <Button
          dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
          size={"medium"}
          variant="progressive"
          onClick={() => onClickNext()}
          disabled={isNextDisabled}
          testId={`${testIdSuffix}-next`}
          shortcut={isGlobalHeaderEnabled ? shiftEnterShortcut : undefined}
          type="fill"
        >
          {t("common:next")}
        </Button>
      );
    } else {
      if (customFooterButton) return customFooterButton;

      // show add button if resource selection is true
      if (resourceSelection) {
        return (
          <Button
            dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
            size={"medium"}
            variant="progressive"
            onClick={onResourceSelectionDone}
            disabled={isNextDisabled}
            testId={`${testIdSuffix}-add`}
            shortcut={isGlobalHeaderEnabled ? shiftEnterShortcut : undefined}
            type="fill"
          >
            {t("common:add")}
          </Button>
        );
      }

      // from unit plan always show save button
      if (!renderAsComponent) {
        return (
          <Button
            dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
            size={"medium"}
            variant="progressive"
            onClick={onClickSave}
            testId={`${testIdSuffix}-save`}
            shortcut={isGlobalHeaderEnabled ? shiftEnterShortcut : undefined}
            type="fill"
          >
            {t("common:save")}
          </Button>
        );
      }

      return (
        <>
          {isAITutor ? (
            <Tooltip
              dsVersion={"2.0"}
              showArrow={true}
              tooltip={t("common:student_preview")}
            >
              {isAITutorModalitiesEnabled && windowWidth >= 1400 ? (
                <Button
                  testId={`${testIdSuffix}-student-preview`}
                  variant={"neutral"}
                  type={"outlined"}
                  dsVersion={"2.0"}
                  onClick={toggleAITutorStudentPreviewModal}
                  icon={<EyeOutlined />}
                >
                  {t("common:student_preview")}
                </Button>
              ) : (
                <IconButton
                  testId={`${testIdSuffix}-student-preview`}
                  variant={"neutral"}
                  type={"outlined"}
                  dsVersion={"2.0"}
                  onClick={toggleAITutorStudentPreviewModal}
                  icon={<EyeOutlined />}
                />
              )}
            </Tooltip>
          ) : null}
          <Button
            dsVersion={"2.0"}
            variant={hideAssignButton ? "progressive" : "neutral"}
            type={hideAssignButton ? "fill" : "outlined"}
            size={"medium"}
            onClick={onClickReassignOrSaveAsDraft}
            testId={`${testIdSuffix}-${
              isDraftMode ? "saveAsDraft" : "editSettings"
            }`}
            disabled={isGeneratingAIQuestions}
          >
            <span
              className={
                shouldTruncateSaveAsDraftText
                  ? classes.saveAsDraftButton
                  : undefined
              }
            >
              {isDraftMode
                ? hideAssignButton
                  ? t("common:save")
                  : t("common:save_as_draft")
                : t("classRoom:edit_settings")}
            </span>
          </Button>
          {!hideAssignButton ? (
            <Button
              dsVersion={"2.0"}
              size={"medium"}
              variant="progressive"
              type="fill"
              onClick={onClickAssignOrUpdate}
              testId={`${testIdSuffix}-${isDraftMode ? "assign" : "update"}`}
              disabled={isGeneratingAIQuestions || isExtracting}
              shortcut={!currentDialogueAction ? shiftEnterShortcut : undefined}
            >
              {isDraftMode ? t("common:assign") : t("common:update")}
            </Button>
          ) : null}
        </>
      );
    }
  };

  return (
    <div className={classes.container} style={containerStyle}>
      <div className={classes.innerContainer} style={innerContainerStyle}>
        {showCancelButton ? (
          <div className={classes.leftContainer}>{renderFooterLeft()}</div>
        ) : null}
        <div className={classes.rightContainer} style={rightContainerStyle}>
          {renderFooterRight()}
        </div>
      </div>
      {showMaxScoreChangedModal ? renderMaxScoreChangedModal() : null}
      {!_.isEmpty(missingCriteriaModalData)
        ? renderMissingCriteriaModal()
        : null}

      {showFullScreenLoaderLocal ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapActionCreators = {
  isMaxScoreValid,
  updateAIDataInRedux,
  validateAIEvaluationEligibility,
  updateTitleFromLTIAttachment,
};

const mapStateToProps = (state, ownProps) => {
  const { assessmentId } = ownProps;
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const aiTutorSelectedTab = _.get(
    state,
    "assessment.aiData.aiTutorSelectedTab"
  );

  const aiData = _.get(state, "assessment.aiData", {});
  const showAITutorStudentPreviewModal = _.get(
    aiData,
    "showAITutorStudentPreviewModal",
    false
  );
  const isToddleLibraryPortal =
    _.get(state, "login.activeTab") === ACTIVE_TAB_TODDLE_LIBRARY;

  const isAITutorUpdated = _.get(aiData, "isAITutorUpdated", false);

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const isExtracting = _.get(
    state,
    `worksheet.extractFromDoc.${assessmentId}.isExtracting`,
    false
  );

  const selectedLearningCourse = getSelectedLearningCourse(state);
  const isBlueprintCourse = getIsBlueprintCourse(selectedLearningCourse);
  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

  return {
    initialMaxScore: _.get(state, "assessment.assessmentData.maxScore", null),
    isGeneratingAIQuestions,
    collaboratorsFilter,
    showAITutorStudentPreviewModal,
    aiTutorSelectedTab,
    isAITutorUpdated,
    isExtracting,
    isBlueprintCourse,
    isLearningCoursesEnabled: getIsLearningCoursesEnabled(),
    isQueriesV2Enabled,
  };
};

export default connect(mapStateToProps, mapActionCreators)(CompactUIFooter);
